import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import axios from 'axios';
import firebase from 'firebase/app';
import { compact } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Check, X } from 'react-feather';
import { CLOUD_FUNCTION_DOMAIN, FUNCTIONS } from '../constants';
import { useSnackbar } from '../hooks/useSnackbar';
import { createSubmission } from '../services/submission';
import { useAllTopics } from '../services/topic';
import styled from '../styled';
import { Submission, SubmissionTopic } from '../types/Submission';
import { Topic } from '../types/Topic';
import { AuthorBadge, Author } from './ProAuthor';
import { HighlightWhite } from './Highlight';

const InfoMessage = styled('div')`
  background: linear-gradient(130.46deg, #0050b3 4.34%, #1890ff 99.39%);
  box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.1);
  line-height: 30px;
  border-radius: 8px;
  padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(4)}px
    ${(p) => p.theme.spacing(3)}px;
`;

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  return debouncedValue;
};

type SSRSafeSubmission = Omit<Submission, 'createdAt'> & {
  createdAt?: firebase.firestore.Timestamp;
};

export const EMPTY_SUBMISSION = (): SSRSafeSubmission => ({
  name: '',
  description: '',
  topics: [],
  authorName: '',
  url: '',
  twitterHandle: '',
  status: 'AWAITING_APPROVAL',
  acceptedAt: null
});

const StatusIconWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: black;
  border-radius: 100%;
`;

const UrlStatus = ({
  loading,
  value,
  error
}: {
  loading: boolean;
  value: string;
  error: boolean;
}) => {
  const Icon = (function () {
    if (loading) {
      return <CircularProgress size={16} />;
    }
    if (error) {
      return (
        <StatusIconWrapper style={{ backgroundColor: '#ff4d4f' }}>
          <X size={14} />
        </StatusIconWrapper>
      );
    }
    if (value.length < 'https://'.length) {
      return '';
    }
    return (
      <StatusIconWrapper style={{ backgroundColor: '#25D787' }}>
        <Check size={14} />
      </StatusIconWrapper>
    );
  })();

  return <InputAdornment position="start">{Icon}</InputAdornment>;
};

export const SubmitBlogForm = ({
  value,
  onSubmit,
  onDelete,
  onCancel,
  label,
  mode = 'create'
}: {
  value: SSRSafeSubmission;
  onSubmit: (nextValue: SSRSafeSubmission) => Promise<any>;
  onDelete?: () => Promise<any>;
  onCancel: () => void;
  mode?: 'create' | 'edit';
  label: string;
}) => {
  const topics = useAllTopics();
  const getTopicsFromValue = () =>
    compact(value.topics.map((t) => topics.find((a) => a.id === t)));

  const { enqueueSnackbar } = useSnackbar();
  const [submission, setSubmission] = useState({ ...value });
  const [submissionTopics, setSubmissionTopics] = useState<Topic[]>(
    getTopicsFromValue()
  );
  const [submissionTopicText, setSubmissionTopicText] = useState('');

  const [urlError, setUrlError] = useState(false);
  const [loadingUrlError, setLoadingUrlError] = useState(false);
  const debouncedSubmissionUrl = useDebounce(submission.url, 500);

  useEffect(() => {
    if (debouncedSubmissionUrl && mode === 'create') {
      setLoadingUrlError(true);
      axios
        .post(`${CLOUD_FUNCTION_DOMAIN}/${FUNCTIONS.checkIfUrlExists}`, {
          url: debouncedSubmissionUrl
        })
        .then(() => {
          setUrlError(false);
          setLoadingUrlError(false);
        })
        .catch(() => {
          setUrlError(true);
          setLoadingUrlError(false);
        });
    }
  }, [debouncedSubmissionUrl, mode]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        const nextSubmission: SSRSafeSubmission = {
          ...submission,
          topics: [
            ...submissionTopics.map((topic) => topic.id as SubmissionTopic)
          ]
        };

        return onSubmit(nextSubmission)
          .then(() => {
            enqueueSnackbar('Submission added! Thanks so much ✨', {
              variant: 'success'
            });
            setSubmission({ ...value });
            setSubmissionTopics(getTopicsFromValue());

            if (mode === 'edit') {
              onCancel();
            }
          })
          .catch(() => {
            enqueueSnackbar('Uh oh, something went wrong.', {
              variant: 'error'
            });
          });
      }}
    >
      <Typography variant="body1" component="p">
        Step 1. Check your recommendation isn't in our database already
      </Typography>
      <TextField
        type="url"
        variant="outlined"
        value={submission.url}
        error={urlError}
        onChange={(e) => {
          setSubmission({ ...submission, url: e.target.value });
        }}
        label="URL of the Blog's index page"
        helperText={
          urlError
            ? 'This blog is already in our database! Recommend another blog?'
            : 'e.g. https://bloggingfordevs.com/blog/'
        }
        InputProps={{
          endAdornment: (
            <UrlStatus
              loading={loadingUrlError}
              value={submission.url}
              error={urlError}
            />
          )
        }}
        placeholder="https://bloggingfordevs.com/blog/"
        margin="normal"
        fullWidth
      />
      <Typography variant="body1" component="p" style={{ marginTop: '18px' }}>
        Step 2. Tell us a little about the blog
      </Typography>
      <TextField
        variant="outlined"
        label="Name of the Blog"
        value={submission.name}
        onChange={(e) => {
          setSubmission({ ...submission, name: e.target.value });
        }}
        helperText="e.g. Blogging for Devs or Jane Doe's Blog"
        placeholder="Jane Doe's Blog"
        margin="normal"
        fullWidth
      />
      <InfoMessage style={{ margin: '12px 0' }}>
        <p>
          <strong>Submissions with poor descriptions will be ignored!</strong>{' '}
          It's not possible for me to personally rewrite descriptions for 100s
          of blogs. Please help me by including information like:
        </p>
        <ul>
          <li>
            Which subjects, programming languages, frameworks, or concepts does
            the author covered most often?
          </li>
          <li>Who should read this blog and what will they learn?</li>
          <li>What makes the blog unique or worth reading?</li>
        </ul>
        <p>FOLLOW THE EXAMPLE. Your effort is appreciated, thank you! 🙌</p>
      </InfoMessage>
      <TextField
        type="description"
        variant="outlined"
        value={submission.description}
        onChange={(e) => {
          setSubmission({ ...submission, description: e.target.value });
        }}
        label="Several descriptive sentences about the blog"
        multiline
        rows={6}
        helperText={
          <>
            <strong>Example:</strong> Jane writes about frontend development
            with React.js and Vue.js. She also writes about frontend testing
            with tools like Cypress and creating pixel-perfect designs with
            React and CSS. Her blog's design and interactive elements made with
            SVG are what make it unique.
          </>
        }
        placeholder="What kinds of topics does this blog cover? What makes it unique and worth reading?"
        margin="normal"
        fullWidth
      />
      <Autocomplete
        multiple
        id="topics"
        options={topics}
        getOptionLabel={(topic) => topic.name}
        inputValue={submissionTopicText}
        onInputChange={(_, v) => setSubmissionTopicText(v)}
        onChange={(_, newValue) => {
          setSubmissionTopics(newValue);
        }}
        value={submissionTopics}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Blog's Main Topics (Pick Up to 2)"
            margin="normal"
            error={submissionTopics.length > 2}
            helperText={
              submissionTopics.length > 2
                ? 'You picked too many topics! Narrow it to two, please.'
                : ''
            }
            placeholder="Choose the blog's main topics"
          />
        )}
      />
      <br />
      <Typography variant="body1" component="p">
        Step 3. Tell us about the author
      </Typography>
      <TextField
        variant="outlined"
        value={submission.authorName}
        onChange={(e) => {
          setSubmission({ ...submission, authorName: e.target.value });
        }}
        label="Author or Editor's Name"
        helperText=""
        placeholder="Jane Doe"
        margin="normal"
        fullWidth
      />
      <TextField
        variant="outlined"
        value={submission.twitterHandle}
        onChange={(e) => {
          setSubmission({ ...submission, twitterHandle: e.target.value });
        }}
        label="Author or Editor's Twitter Handle"
        InputProps={{
          startAdornment: <InputAdornment position="start">@</InputAdornment>
        }}
        helperText=""
        placeholder="bloggingfordevs"
        margin="normal"
        fullWidth
      />
      <div style={{ marginTop: '24px' }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={
            !submission.name ||
            !submission.description ||
            !submission.url ||
            !submission.authorName ||
            urlError ||
            submissionTopics.length > 2 ||
            submissionTopics.length === 0
          }
        >
          {label}
        </Button>
        {onDelete && (
          <Button
            size="large"
            color="secondary"
            variant="text"
            style={{ marginLeft: '12px' }}
            onClick={() => {
              onDelete()
                .then(onCancel)
                .then(() => {
                  enqueueSnackbar('Deleted submission', {
                    variant: 'success'
                  });
                });
            }}
          >
            Reject and delete
          </Button>
        )}
      </div>
    </form>
  );
};

export const SubmitBlogDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="body">
      <DialogTitle>Recommend your favorite blogs by folks in tech!</DialogTitle>
      <DialogContent>
        <InfoMessage>
          <p>
            <strong>Head's up:</strong> Blogs need a track record of valuable,
            original content, and must be written primarily by an individual, as
            opposed to companies, agencies, aggregators, or group publications.
            All blogs are checked for quality by a human (that would be me!).
            <br />
            <br />
            Please also consider{' '}
            <HighlightWhite>
              submitting a blog by a woman or member of an underrepresented
              group in tech
            </HighlightWhite>{' '}
            before adding your own. Thanks for your help!
          </p>
          <Author
            color="blue"
            name="Monica Lent"
            byline={
              <span>
                <em>The person checking your submissions manually </em>😉
              </span>
            }
            img="/images/my-face.jpg"
            badge={<AuthorBadge>Creator</AuthorBadge>}
          />
        </InfoMessage>
        <br />
        <SubmitBlogForm
          value={EMPTY_SUBMISSION()}
          onSubmit={(submission) =>
            createSubmission({
              ...submission,
              createdAt: firebase.firestore.Timestamp.now()
            })
          }
          onCancel={onClose}
          label="Submit recommendation"
        />
      </DialogContent>
    </Dialog>
  );
};
